import React from 'react';
import { Tag } from 'antd';

/*
| Label colours, rendered SOLID.
|
| Ant's preset tag names ("blue", "red") render a pale tinted background with coloured
| text — fine for a status hint, weak for a label, which has to be picked out of a
| crowded card at a glance. These are the same twelve tones as solid fills.
|
| Fixed hex is the right call HERE, unlike elsewhere in the app: a saturated fill with
| white text reads correctly on both the light and dark canvas, because the label paints
| its own background rather than borrowing the page's. That is also why Trello labels look
| identical in either theme.
|
| Stored values stay the tone NAME ("blue"), not the hex, so the palette can be retuned
| in one place without rewriting anyone's data.
*/
const SOLID = {
    default:  { bg: '#8c8c8c', fg: '#ffffff' },
    blue:     { bg: '#1677ff', fg: '#ffffff' },
    cyan:     { bg: '#13c2c2', fg: '#ffffff' },
    green:    { bg: '#389e0d', fg: '#ffffff' },
    // Light fills need dark text — white on lime or gold fails contrast badly.
    lime:     { bg: '#a0d911', fg: '#1f1f1f' },
    gold:     { bg: '#faad14', fg: '#1f1f1f' },
    orange:   { bg: '#fa8c16', fg: '#ffffff' },
    volcano:  { bg: '#fa541c', fg: '#ffffff' },
    red:      { bg: '#cf1322', fg: '#ffffff' },
    magenta:  { bg: '#c41d7f', fg: '#ffffff' },
    purple:   { bg: '#722ed1', fg: '#ffffff' },
    geekblue: { bg: '#2f54eb', fg: '#ffffff' },
};

/** The tone names offered when picking a colour. */
export const LABEL_TONES = [
    { value: 'default', label: 'Grey' },
    { value: 'blue', label: 'Blue' },
    { value: 'cyan', label: 'Cyan' },
    { value: 'green', label: 'Green' },
    { value: 'lime', label: 'Lime' },
    { value: 'gold', label: 'Gold' },
    { value: 'orange', label: 'Orange' },
    { value: 'volcano', label: 'Volcano' },
    { value: 'red', label: 'Red' },
    { value: 'magenta', label: 'Magenta' },
    { value: 'purple', label: 'Purple' },
    { value: 'geekblue', label: 'Indigo' },
];

/**
 * Resolve a stored colour to a solid fill.
 *
 * Older labels were saved as raw hex. Those are honoured as the fill directly, with white
 * text — the value is theirs and guessing a replacement tone would silently change what
 * someone chose.
 */
export const solidLabelStyle = (color) => {
    if (color && color.startsWith('#')) {
        return { background: color, color: '#ffffff', borderColor: color };
    }

    const tone = SOLID[color] || SOLID.default;

    return { background: tone.bg, color: tone.fg, borderColor: tone.bg };
};

/**
 * A label, everywhere it appears — cards, the detail panel, the pickers, the manager.
 *
 * One component so the four surfaces cannot drift; they previously each built their own
 * <Tag> and the pickers had already fallen out of step with the cards.
 */
const TodoLabelTag = ({ color, children, style, ...rest }) => (
    <Tag
        {...rest}
        style={{
            ...solidLabelStyle(color),
            // Solid fills carry their own weight; a border would read as a second edge.
            border: 'none',
            fontWeight: 500,
            ...style,
        }}
    >
        {children}
    </Tag>
);

export default TodoLabelTag;
